// src/index.tsx

import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { TenantConfigProvider } from "./contexts/TenantConfigContext";
import { HelmetProvider } from 'react-helmet-async';
import { Amplify } from "aws-amplify"; // Import Amplify from @aws-amplify/core
import { AuthProvider } from "./contexts/AuthContext";

// Configure Amplify with thse settings

const authConfig: any = {
  Cognito: {
    // Prefer generic REACT_APP_USER_POOL_* first so staging doesn't get overridden
    userPoolId: import.meta.env.VITE_USER_POOL_ID || import.meta.env.VITE_COGNITO_USER_POOL_ID || "",
    userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID || import.meta.env.VITE_COGNITO_CLIENT_ID || "",
  },
};

const identityPoolId = import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID || import.meta.env.VITE_IDENTITY_POOL_ID;
if (identityPoolId) {
  authConfig.Cognito.identityPoolId = identityPoolId;
}

const appsyncApiUrl = import.meta.env.VITE_APPSYNC_API_URL || import.meta.env.VITE_APPSYNC_URL;

const graphQlConfig = appsyncApiUrl ? {
  endpoint: appsyncApiUrl,
  region: import.meta.env.VITE_APPSYNC_REGION || import.meta.env.VITE_AWS_REGION || 'af-south-1',
  defaultAuthMode: 'userPool' as const,
} : undefined;

Amplify.configure({
  Auth: authConfig,
  ...(graphQlConfig ? {
    API: {
      GraphQL: graphQlConfig,
    }
  } : {})
});

// Apply theme overrides from environment variables (HSL triplets like "210 33% 20%")
const setCssVar = (name: string, value?: string) => {
  if (!value) return;
  document.documentElement.style.setProperty(name, value);
};

// Design tokens from environment variables (HSL triplets)
const THEME_COLOR_PRIMARY_HSL = import.meta.env.VITE_THEME_COLOR_PRIMARY_HSL;
const THEME_COLOR_ACCENT_HSL  = import.meta.env.VITE_THEME_COLOR_ACCENT_HSL;
const THEME_BG_APP_HSL        = import.meta.env.VITE_THEME_BG_APP_HSL;
const THEME_BG_SURFACE_HSL    = import.meta.env.VITE_THEME_BG_SURFACE_HSL;
const THEME_TEXT_PRIMARY_HSL  = import.meta.env.VITE_THEME_TEXT_PRIMARY_HSL;
const THEME_TEXT_MUTED_HSL    = import.meta.env.VITE_THEME_TEXT_MUTED_HSL;
const THEME_BORDER_HSL        = import.meta.env.VITE_THEME_BORDER_HSL;
const THEME_BG_LOGIN_HSL      = import.meta.env.VITE_THEME_BG_LOGIN_HSL;

// Map tokens to existing CSS variables (non-breaking); can be renamed later in CSS
setCssVar('--primary', THEME_COLOR_PRIMARY_HSL);
setCssVar('--accent', THEME_COLOR_ACCENT_HSL);
setCssVar('--ring', THEME_COLOR_ACCENT_HSL);
setCssVar('--sidebar-background', THEME_BG_APP_HSL);
setCssVar('--sidebar-foreground', THEME_TEXT_PRIMARY_HSL);
setCssVar('--sidebar-primary', THEME_COLOR_ACCENT_HSL);
setCssVar('--sidebar-accent', THEME_BG_SURFACE_HSL);
setCssVar('--sidebar-border', THEME_BORDER_HSL);
setCssVar('--login-bg', THEME_BG_LOGIN_HSL);
// Optional extra token exposure if CSS supports it now or later
setCssVar('--text-muted', THEME_TEXT_MUTED_HSL);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <HelmetProvider>
    <AuthProvider>
      <TenantConfigProvider>
        <App />
      </TenantConfigProvider>
    </AuthProvider>
  </HelmetProvider>
);
